"use client";

import {
  AnimatePresence,
  motion,
  useMotionValue,
  type Transition,
  type Variant,
} from "motion/react";
import { useEffect, useRef, useState, type ReactNode } from "react";

type CursorVariants = {
  initial: Variant;
  animate: Variant;
  exit: Variant;
};

export function Cursor({
  attachToParent = false,
  children,
  className = "",
  transition,
  variants,
}: {
  attachToParent?: boolean;
  children: ReactNode;
  className?: string;
  transition?: Transition;
  variants?: CursorVariants;
}) {
  const cursorX = useMotionValue(0);
  const cursorY = useMotionValue(0);
  const cursorRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(!attachToParent);

  useEffect(() => {
    cursorX.set(window.innerWidth / 2);
    cursorY.set(window.innerHeight / 2);
  }, [cursorX, cursorY]);

  useEffect(() => {
    const updatePosition = (event: MouseEvent) => {
      cursorX.set(event.clientX);
      cursorY.set(event.clientY);
    };

    document.addEventListener("mousemove", updatePosition);
    return () => document.removeEventListener("mousemove", updatePosition);
  }, [cursorX, cursorY]);

  useEffect(() => {
    if (!attachToParent) return;
    const parent = cursorRef.current?.parentElement;
    if (!parent) return;

    const show = () => {
      parent.style.cursor = "none";
      setIsVisible(true);
    };
    const hide = () => {
      parent.style.cursor = "auto";
      setIsVisible(false);
    };

    parent.addEventListener("mouseenter", show);
    parent.addEventListener("mouseleave", hide);
    return () => {
      parent.removeEventListener("mouseenter", show);
      parent.removeEventListener("mouseleave", hide);
      parent.style.cursor = "auto";
    };
  }, [attachToParent]);

  return (
    <motion.div
      ref={cursorRef}
      className={`cursor-follow ${className}`}
      style={{ x: cursorX, y: cursorY, translateX: "-50%", translateY: "-50%" }}
    >
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial="initial"
            animate="animate"
            exit="exit"
            variants={variants}
            transition={transition}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
